import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'src/store';
import { parseValidatedAtParam } from 'src/shared/utils/timePeriodHelpers';
import { setSelectedAt as setSelectedAtAction } from 'src/app/portfolio/views/global-nature-risk/state/gnrFiltersSlice';
import { getSelectedAt } from 'src/app/portfolio/views/global-nature-risk/state/gnrFiltersSelector';

const AT_PARAM = 'at';

// The GNR time period filter persists across portfolio and site-level views alike, unlike every
// other GNR filter — so it lives in a single shared Redux slot (gnrFiltersSlice.selectedAt) rather
// than being scoped to a route family. Portfolio views read/write it via useGnrFilters, which layers
// URL sync on top for shareable/bookmarkable state; this hook does the same for the site-level route
// tree, which has no other query params to worry about — just the one.
const useSelectedTimePeriod = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const dispatch = useAppDispatch();
    const selectedAtFromRedux = useAppSelector(getSelectedAt);

    // Kept current so the effect can read Redux state without it being a dependency.
    const selectedAtRef = useRef(selectedAtFromRedux);
    selectedAtRef.current = selectedAtFromRedux;

    useEffect(() => {
        const urlAt = parseValidatedAtParam(searchParams, AT_PARAM);
        if (urlAt) {
            // URL wins: sync it into Redux (handles initial load, back/forward to a shared link).
            dispatch(setSelectedAtAction(urlAt));
        } else if (selectedAtRef.current) {
            // URL is empty — restore from Redux so a hard refresh or copy/paste of the URL keeps
            // the selection, matching every other GNR filter's persistence behaviour.
            const next = new URLSearchParams(searchParams);
            next.set(AT_PARAM, selectedAtRef.current);
            setSearchParams(next, { replace: true });
        }
    }, [searchParams, dispatch, setSearchParams]);

    const selectedAt = parseValidatedAtParam(searchParams, AT_PARAM) ?? selectedAtFromRedux;

    return {
        selectedAt,
        setSelectedAt: (at: string | null) => {
            const next = new URLSearchParams(searchParams);
            if (at) {
                next.set(AT_PARAM, at);
            } else {
                next.delete(AT_PARAM);
            }
            setSearchParams(next);
            dispatch(setSelectedAtAction(at));
        },
    };
};

export default useSelectedTimePeriod;
